import { bind } from '@react-rxjs/core'
import {
  distinctUntilChanged,
  filter,
  fromEventPattern,
  map,
  merge,
  Observable,
  of,
  shareReplay,
  switchMap,
  takeUntil,
  timeout,
} from 'rxjs'

import { logAndCaptureException } from '../../utils/errorTools'
import {
  isKetchPreferenceExperience,
  KetchExperienceDisplay,
  KetchInternalApiError,
  mkGetConfigDisplayCallback,
  mkGetIsDisplayedCallback,
} from './internal'

export interface KetchExperienceState {
  /** Whether any Ketch experience is on screen, a consent banner included. */
  isDisplayed: boolean
  /**
   * Whether that experience covers the page, which is what our own modals gate on. A banner leaves
   * the page usable; every other display type, and one we cannot read, does not.
   *
   * A banner yields to our modals rather than the other way round, which takes a CSS rule as well
   * as this flag: Ketch fixes the banner at the 32-bit maximum z-index, so it would otherwise paint
   * over a dialog while inheriting the `pointer-events: none` Radix puts on the body. See the
   * `#ketch-banner` override in `styles/global.css`.
   */
  coversPage: boolean
}

const hiddenState: KetchExperienceState = { isDisplayed: false, coversPage: false }

/** What we assume when the display type can't be read: keep out of the way. */
const coveringState: KetchExperienceState = { isDisplayed: true, coversPage: true }

const ketchEvent$ = (event: 'willShowExperience' | 'hideExperience'): Observable<unknown> =>
  fromEventPattern(
    (handler) => {
      if (typeof window?.ketch === 'function') {
        window.ketch('on', event, handler)
      }
    },
    (handler) => {
      if (typeof window?.ketch === 'function') {
        window.ketch('off', event, handler)
      }
    },
    // Ketch passes the experience type first. Without this, a second argument added later would turn
    // the emission into an array and the preference-centre check would miss.
    (experienceType: unknown) => experienceType,
  )

/** A one-shot Ketch getter answered through its callback. Completes empty when the tag isn't loaded. */
const ketchRead$ = <T>(
  read: (ketch: NonNullable<Window['ketch']>, callback: (value: T) => void) => void,
): Observable<T> =>
  new Observable<T>((subscriber) => {
    if (typeof window?.ketch === 'function') {
      read(window.ketch, (value) => {
        subscriber.next(value)
        subscriber.complete()
      })
    } else {
      subscriber.complete()
    }
  })

/**
 * The visitor's region from Ketch's IP lookup, as `country-region` (`US-CA`). Answered even when
 * asked after the tag has loaded, and not at all when it never loads (an ad blocker, say).
 */
export const ketchRegionInfo$: Observable<string> = ketchRead$(
  (ketch, callback: (regionInfo: unknown) => void) => ketch('getRegionInfo', callback),
).pipe(filter((regionInfo): regionInfo is string => typeof regionInfo === 'string'))

const experienceDisplay$ = ketchRead$(
  (ketch, callback: (display: KetchExperienceDisplay | undefined) => void) =>
    ketch('getConfig', mkGetConfigDisplayCallback(callback)),
)

const isDisplayedAtBoot$ = ketchRead$((ketch, callback: (isDisplayed: boolean) => void) =>
  ketch('getIsDisplayed', mkGetIsDisplayedCallback(callback)),
)

/**
 * How long to wait for `getConfig` before assuming the experience covers the page. A getter that
 * stopped answering would otherwise disable every suppression on the site.
 */
const CONFIG_READ_TIMEOUT_MS = 2_000

/**
 * The consent experience's display type, read once and replayed for the session. Only a consent
 * experience reaches this read, and its display type is property config that does not change.
 *
 * Deliberately not eager: at import the Ketch tag usually has not loaded, and the read would
 * complete with no value and poison the replay.
 */
const consentDisplay$ = experienceDisplay$.pipe(
  timeout({
    first: CONFIG_READ_TIMEOUT_MS,
    with: () => {
      logAndCaptureException(
        new KetchInternalApiError(
          `getConfig did not answer within ${CONFIG_READ_TIMEOUT_MS}ms; assuming the experience covers the page`,
        ),
      )
      return of(undefined)
    },
  }),
  shareReplay({ bufferSize: 1, refCount: false }),
)

const displayedState$ = consentDisplay$.pipe(
  map((display): KetchExperienceState => ({ isDisplayed: true, coversPage: display !== 'banner' })),
)

/**
 * Suppression has to start in the tick of the event: waiting for the display type would leave a
 * gated modal rendered over a Ketch overlay for the length of the read, which is the direction that
 * fails unsafe.
 *
 * Because `consentDisplay$` is replayed, it answers synchronously on subscribe from the second
 * experience on, and `takeUntil` then drops the covering state so only the answer is emitted. www
 * runs React 17, which renders each store notification on its own, so a covering state followed by
 * the relaxed one in the same tick would still flip a gated `Dialog` closed and open. Only an event
 * arriving before the read has landed pays a visible suppression, and that one errs in the safe
 * direction.
 */
const readDisplayState$ = merge(displayedState$, of(coveringState).pipe(takeUntil(displayedState$)))

/** Kept a union so the case needing no config read stays distinguishable from the one that does. */
type ExperienceSignal =
  | { _tag: 'hidden' }
  /** Displayed, and known to cover the page from the experience type alone. */
  | { _tag: 'covering' }
  /** Displayed, with the display type still to be read from the config. */
  | { _tag: 'readDisplay' }

const signalForShow = (experienceType: unknown): ExperienceSignal =>
  isKetchPreferenceExperience(experienceType) ? { _tag: 'covering' } : { _tag: 'readDisplay' }

const stateOf = (signal: ExperienceSignal): Observable<KetchExperienceState> => {
  switch (signal._tag) {
    case 'hidden':
      return of(hiddenState)
    case 'covering':
      return of(coveringState)
    case 'readDisplay':
      return readDisplayState$
  }
}

const isSameState = (a: KetchExperienceState, b: KetchExperienceState): boolean =>
  a.isDisplayed === b.isDisplayed && a.coversPage === b.coversPage

/**
 * Monitor whether a Ketch experience is currently displayed, and whether it covers the page.
 *
 * The boot read and the events feed one stream so a single `switchMap` covers both: a
 * `hideExperience` arriving while a config read is in flight cancels it, where a separate branch
 * would let the stale result land after the hide with no further event to correct it. The boot read
 * carries no experience type, so it takes the same path as a consent experience.
 */
export const [useKetchExperienceState, ketchExperienceState$] = bind<KetchExperienceState>(
  merge(
    isDisplayedAtBoot$.pipe(
      map(
        (isDisplayed): ExperienceSignal =>
          isDisplayed ? { _tag: 'readDisplay' } : { _tag: 'hidden' },
      ),
    ),
    ketchEvent$('willShowExperience').pipe(map(signalForShow)),
    ketchEvent$('hideExperience').pipe(map((): ExperienceSignal => ({ _tag: 'hidden' }))),
  ).pipe(switchMap(stateOf), distinctUntilChanged(isSameState)),
  hiddenState,
)
